import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CapacitorHttp } from '@capacitor/core';
import { LanguageService } from './language.service';
import { SelectorConverterService, SelectorConverterConfig } from './selector-converter.service';
import { ScaniaSelectOption } from '../../components/scania-selector/scania-selector.component';
import { environment } from '../../environments/environment';

/**
 * Estructura del JSON de valores de selector
 */
export interface SelectorValuesData {
  generatedAt: number[];
  version: string;
  language: string;
  provinces: Record<string, any[]>;
  towns: Record<string, any[]>;
  settingValues: Record<string, { typeCode: number; typeName: string; values: any[] }>;
  roadTypes: any[];
  vehicleTypes: Record<string, string>;
  propulsions: Record<string, string>;
  vehicleStatuses: Record<string, string>;
  internalUseStatuses: Record<string, string>;
  checklistBlocks: Record<string, string>;
  checklistTypes: Record<string, string>;
  userProfiles: Record<string, string>;
  personTypes: Record<string, string>;
  systemRoles: Record<string, string>;
}

/**
 * Códigos de tipos de settingValues
 */
export enum SettingValueType {
  /** Marca del camión */
  TRUCK_BRAND = 10,
  /** Modelo del camión */
  TRUCK_MODEL = 20,
  /** Check list exterior 24h (Asistencia) */
  CHECKLIST_EXTERIOR_24H = 30,
  /** Check list interior 24h (Asistencia) */
  CHECKLIST_INTERIOR_24H = 40,
  /** Check list exterior recambio */
  CHECKLIST_EXTERIOR_RECAMBIO = 50,
  /** Check list interior recambio */
  CHECKLIST_INTERIOR_RECAMBIO = 60,
  /** Check list exterior cortesía */
  CHECKLIST_EXTERIOR_CORTESIA = 70,
  /** Check list interior cortesía */
  CHECKLIST_INTERIOR_CORTESIA = 80
}

@Injectable({
  providedIn: 'root'
})
export class SelectorDataService {
  private languageService = inject(LanguageService);
  private selectorConverter = inject(SelectorConverterService);

  /** Datos cargados por idioma */
  private dataCache: Map<string, SelectorValuesData> = new Map();
  
  /** Subject para notificar cuando los datos están listos */
  private dataLoaded$ = new BehaviorSubject<boolean>(false);
  
  /** Datos actualmente activos según el idioma */
  private currentData: SelectorValuesData | null = null;

  /** Flag para controlar si es la primera carga */
  private isInitialLoad = true;

  constructor() {
    // NO cargar aquí automáticamente
    // La carga se debe iniciar desde app.component.ts

    // Suscribirse a cambios de idioma
    this.languageService.languageChanged$.subscribe((lang: string) => {
      this.loadData(lang, false); // Usar caché en cambios de idioma
    });
  }

  /**
   * Inicializa la carga de datos. Debe llamarse al arrancar la app.
   * Siempre carga desde el servidor en la primera llamada.
   */
  async initialize(): Promise<void> {
    const currentLang = LanguageService.getCurrentLanguage();
    await this.loadData(currentLang, true); // Forzar carga desde servidor
    this.isInitialLoad = false;
  }

  /**
   * Observable que emite cuando los datos están cargados
   */
  get isDataLoaded$(): Observable<boolean> {
    return this.dataLoaded$.asObservable();
  }

  /**
   * Carga los datos para un idioma específico
   * @param language Código del idioma ('es' o 'pt')
   * @param forceReload Si es true, ignora la caché y recarga desde el servidor
   */
  async loadData(language: string, forceReload: boolean = false): Promise<SelectorValuesData | null> {
    // Si ya está en caché y no se fuerza la recarga, usar los datos cacheados
    if (!forceReload && this.dataCache.has(language)) {
      this.currentData = this.dataCache.get(language)!;
      this.dataLoaded$.next(true);
      return this.currentData;
    }

    // Los valores de selectores se piden a la API, que es quien tiene acceso
    // al Blob Storage. El endpoint requiere sesión, así que sin token todavía
    // no se puede cargar (se reintenta al iniciar sesión).
    const token = localStorage.getItem('auth_token');
    if (!token) {
      return null;
    }

    try {
      const response = await CapacitorHttp.get({
        url: `${environment.apiUrl}selector-values?lang=${language}`,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 200 && response.data?.success) {
        // El backend devuelve el contenido del fichero como texto JSON
        const raw = response.data.response;
        const data = (typeof raw === 'string' ? JSON.parse(raw) : raw) as SelectorValuesData;
        this.dataCache.set(language, data);
        this.currentData = data;
        this.dataLoaded$.next(true);
        return data;
      }

      console.error(`Error cargando datos de selector para ${language}: respuesta inesperada`, response.status);
    } catch (error) {
      console.error(`Error cargando datos de selector para ${language}:`, error);
    }

    return null;
  }

  /**
   * Obtiene los datos actuales (según el idioma activo)
   */
  getData(): SelectorValuesData | null {
    return this.currentData;
  }

  // ============================================
  // MÉTODOS DE CONVENIENCIA PARA SELECTORES
  // ============================================

  /**
   * Obtiene las provincias como opciones de selector
   * @param countryId ID del país (opcional, si no se pasa devuelve todas)
   */
  getProvinces(countryId?: number): ScaniaSelectOption[] {
    if (!this.currentData?.provinces) return [];
    
    return this.selectorConverter.fromGroupedObject(
      this.currentData.provinces,
      { sortBy: 'label' },
      countryId?.toString()
    );
  }

  /**
   * Obtiene los municipios como opciones de selector
   * @param provinceId ID de la provincia
   */
  getTowns(provinceId: number): ScaniaSelectOption[] {
    if (!this.currentData?.towns) return [];
    
    return this.selectorConverter.fromGroupedObject(
      this.currentData.towns,
      { sortBy: 'label' },
      provinceId.toString()
    );
  }

  /**
   * Obtiene los tipos de vehículo como opciones de selector
   */
  getVehicleTypes(): ScaniaSelectOption[] {
    if (!this.currentData?.vehicleTypes) return [];
    return this.selectorConverter.fromKeyValue(this.currentData.vehicleTypes);
  }

  /**
   * Obtiene los tipos de propulsión como opciones de selector
   */
  getPropulsions(): ScaniaSelectOption[] {
    if (!this.currentData?.propulsions) return [];
    return this.selectorConverter.fromKeyValue(this.currentData.propulsions);
  }

  /**
   * Obtiene los estados de vehículo como opciones de selector
   */
  getVehicleStatuses(): ScaniaSelectOption[] {
    if (!this.currentData?.vehicleStatuses) return [];
    return this.selectorConverter.fromKeyValue(this.currentData.vehicleStatuses);
  }

  /**
   * Obtiene los estados de uso interno como opciones de selector
   */
  getInternalUseStatuses(): ScaniaSelectOption[] {
    if (!this.currentData?.internalUseStatuses) return [];
    return this.selectorConverter.fromKeyValue(this.currentData.internalUseStatuses);
  }

  /**
   * Obtiene los bloques de checklist como opciones de selector
   */
  getChecklistBlocks(): ScaniaSelectOption[] {
    if (!this.currentData?.checklistBlocks) return [];
    return this.selectorConverter.fromKeyValue(this.currentData.checklistBlocks);
  }

  /**
   * Obtiene los tipos de checklist como opciones de selector
   */
  getChecklistTypes(): ScaniaSelectOption[] {
    if (!this.currentData?.checklistTypes) return [];
    return this.selectorConverter.fromKeyValue(this.currentData.checklistTypes);
  }

  /**
   * Obtiene los perfiles de usuario como opciones de selector
   */
  getUserProfiles(): ScaniaSelectOption[] {
    if (!this.currentData?.userProfiles) return [];
    return this.selectorConverter.fromKeyValue(this.currentData.userProfiles);
  }

  /**
   * Obtiene los tipos de persona como opciones de selector
   */
  getPersonTypes(): ScaniaSelectOption[] {
    if (!this.currentData?.personTypes) return [];
    return this.selectorConverter.fromKeyValue(this.currentData.personTypes);
  }

  /**
   * Obtiene los roles del sistema como opciones de selector
   */
  getSystemRoles(): ScaniaSelectOption[] {
    if (!this.currentData?.systemRoles) return [];
    return this.selectorConverter.fromKeyValue(this.currentData.systemRoles);
  }

  /**
   * Obtiene los tipos de carretera como opciones de selector
   */
  getRoadTypes(): ScaniaSelectOption[] {
    if (!this.currentData?.roadTypes) return [];
    return this.selectorConverter.fromArray(this.currentData.roadTypes);
  }

  /**
   * Obtiene las marcas de camión como opciones de selector
   */
  getTruckBrands(): ScaniaSelectOption[] {
    if (!this.currentData?.settingValues) return [];
    return this.selectorConverter.fromSettingValues(
      this.currentData.settingValues,
      SettingValueType.TRUCK_BRAND,
      { sortBy: 'label' }
    );
  }

  /**
   * Obtiene los modelos de camión como opciones de selector
   * @param brandId ID de la marca para filtrar (opcional)
   */
  getTruckModels(brandId?: number): ScaniaSelectOption[] {
    if (!this.currentData?.settingValues) return [];
    
    const config: SelectorConverterConfig = { sortBy: 'label' };
    
    if (brandId !== undefined) {
      config.filterFn = (item) => item.idBrand === brandId;
    }

    return this.selectorConverter.fromSettingValues(
      this.currentData.settingValues,
      SettingValueType.TRUCK_MODEL,
      config
    );
  }

  /**
   * Obtiene los items de checklist para un tipo específico
   * @param type Tipo de checklist (usar SettingValueType)
   * @param block Bloque para filtrar (opcional)
   */
  getChecklistItems(type: SettingValueType, block?: number): ScaniaSelectOption[] {
    if (!this.currentData?.settingValues) return [];
    
    const config: SelectorConverterConfig = { sortBy: 'label' };
    
    if (block !== undefined) {
      config.filterFn = (item) => item.block === block;
    }

    return this.selectorConverter.fromSettingValues(
      this.currentData.settingValues,
      type,
      config
    );
  }

  /**
   * Obtiene valores personalizados de settingValues
   * @param typeCode Código del tipo
   * @param config Configuración adicional
   */
  getSettingValues(typeCode: number, config?: SelectorConverterConfig): ScaniaSelectOption[] {
    if (!this.currentData?.settingValues) return [];
    return this.selectorConverter.fromSettingValues(
      this.currentData.settingValues,
      typeCode,
      config || {}
    );
  }

  /**
   * Método genérico para convertir cualquier dato a opciones de selector
   * @param dataKey Clave de los datos en el JSON
   * @param config Configuración de conversión
   */
  getOptions(dataKey: keyof SelectorValuesData, config?: SelectorConverterConfig): ScaniaSelectOption[] {
    if (!this.currentData) return [];
    
    const data = this.currentData[dataKey];
    
    if (!data) return [];

    // Si es un array, usar fromArray
    if (Array.isArray(data)) {
      return this.selectorConverter.fromArray(data, config);
    }

    // Si es un objeto con valores string (key-value), usar fromKeyValue
    const firstValue = Object.values(data)[0];
    if (typeof firstValue === 'string') {
      return this.selectorConverter.fromKeyValue(data as Record<string, string>);
    }

    // Si es un objeto con arrays (grouped), usar fromGroupedObject
    if (Array.isArray(firstValue)) {
      return this.selectorConverter.fromGroupedObject(data as Record<string, any[]>, config);
    }

    return [];
  }

  /**
   * Limpia la caché de datos
   */
  clearCache(): void {
    this.dataCache.clear();
    this.currentData = null;
    this.dataLoaded$.next(false);
  }

  /**
   * Recarga los datos forzando la recarga desde el servidor
   */
  async reloadData(): Promise<void> {
    this.clearCache();
    const currentLang = LanguageService.getCurrentLanguage();
    await this.loadData(currentLang, true);
  }
}
